import React, {useEffect, useState} from "react"
import { PasswordlessUnenrollment } from "./PasswordlessUnenrollment";
import { Link, Button, Typography } from "@one-thd/sui-atomic-components";
import PasswordEdit from "./PasswordEdit";
import {
  FEATURE_SWITCHES,
  isFeatureSwitchEnabled,
  isNativeApp,
  checkNativeAppVersion,
  isWebAuthnEnabled,
} from "../../../../utils";
import { isEmailEnrolledInWebauthn, isDeviceWebAuthnCapable } from "../../../../utils/webauthnUtils";
import { MESSAGE } from "../../../../constants";
import WebAuthnIcons from "../WebAuthn/WebAuthnIcons.component";
import { WebAuthnEnrollment } from "../WebAuthn/WebAuthnEnrollment.component";
import { getOTPv2 } from "../../../../api/custAccountApi";
import { handleApiError } from "../../../../utils/errors";
import analytics from "../../../../utils/analytics";
import { useNativeWebAuthnData } from "../../NativeWebAuthnProvider";
import {useCustomerAccount} from "../../AccountProvider";

const PasswordDisplay = ({onEditPassword}) => {
  const passwordAutomationId = "login-information--edit-password-link";

  return (
    <div className="col__12-12 u--paddingNone-horizontal">
      <div>
        <div className="col__12-12 col__3-12--xs col__4-12--sm col__4-12--md col__4-12--lg u__bold u--paddingNone u--paddingSmall-right">
          <Typography weight="bold" variant="body-base">
            Password
          </Typography>
        </div>
        <div className="col__10-12 col__7-12--xs col__6-12--sm col__6-12--md col__6-12--lg u--paddingNone u--marginXsmall-top">********</div>
        <div className="col__2-12 col__2-12--xs col__2-12--sm col__2-12--md col__2-12--lg u--paddingNone u__text-align--right">
          <button type="button"
            onClick={onEditPassword}
            data-automation-id={passwordAutomationId}
            className="u--paddingSmall-left u__default-link">
            <Link>Edit</Link>
          </button>
        </div>
      </div>
    </div>
  );
};

const PasswordOptions = () => (
  <div className="col__12-12 u--paddingNone-horizontal">
    <div>
      <div className="col__12-12 u--paddingNone u--paddingXsmall-top security-page-sub-header-text">
        You are currently signing in without a password. This is the recommended option.
      </div>
      <PasswordlessUnenrollment />
    </div>
  </div>
);

const WebAuthnInteractionContainer = ({ errors, openWebAuthnModal, webAuthnGlobalState, email, setWebAuthnGlobalState, setErrors, errorCleanUp, flow, setFlow}) => {
  return (
    <div className="">
      <section className="">
        <div className="sui-text-justify">
          { MESSAGE.WEBAUTHN_EXHORT_MESSAGE }
        </div>
        <WebAuthnIcons />
        <Button fullWidth onClick={ openWebAuthnModal } variant="secondary" > Enable Face or Fingerprint ID </Button>
        <WebAuthnEnrollment
            email={email}
            webAuthnGlobalState={webAuthnGlobalState}
            openWebAuthnModal={openWebAuthnModal}
            setWebAuthnGlobalState={setWebAuthnGlobalState}
            errors={errors}
            flow={flow}
            setFlow={setFlow}
            setErrors={setErrors}
            errorCleanUp={errorCleanUp} />
      </section>
    </div>
  )
}



const PasswordOptionsWebAuthnUpgrade = () => {
  const {email} = useCustomerAccount()
  const [errors, setErrors] = useState([]);
  const errorCleanUp = () => setErrors([]);
  const [flow, setFlow] = useState("");


  const [webAuthnGlobalState, setWebAuthnGlobalState] = useState({
    // Modal States
    isModalLoading: false,
    isOTPValid: false,
    successfulWebauthnDeviceEnrollment: false,
    valueOTP: "",
    isWebAuthnModalOpen: false,
    // Payload
    getOTPEncryptedPayload: null,
    forgeRockScript: null,
    // Resolve / Reject functions
    verifyOTPPromiseResolve: undefined,
    verifyOTPPromiseReject: undefined,
    forgeRockPromiseResolve: undefined,
    forgeRockPromiseReject: undefined,
    // Promises
    verifyOTPWebAuthnPromise: undefined,
    forgeRockScriptPromise: undefined,
    isCurrentDeviceWebAuthnCapable: false,
    // WebAuthn Process
    isWebAuthnEnrolling: false,
    isSubmittingWebAuthnResult: false,
    isEnrollmentUnsuccessful: false,
    isClientError: false,
    frState: undefined,
  });

  useEffect(() => {
    if (!isNativeApp()) {
      isDeviceWebAuthnCapable()
        .then((assessment) => {
          setWebAuthnGlobalState({
            ...webAuthnGlobalState,
            isCurrentDeviceWebAuthnCapable: assessment
          })
        })
    }
  },[]);

  const { isDeviceCompatible, nativeWebAuthnData } = useNativeWebAuthnData();

  const deviceSupportsWebAuthn =
  isNativeApp()
    ? (isDeviceCompatible && checkNativeAppVersion())
	: webAuthnGlobalState.isCurrentDeviceWebAuthnCapable;
  const isWebAuthnEnabledAndCompatible = isWebAuthnEnabled() && deviceSupportsWebAuthn;

  const openWebAuthnModal = () => {
    analytics.postSubmissionEvent("biometric enrollment start");
    setFlow("OTP");
    errorCleanUp();
    let verifyOTPPromiseResolve;
    let verifyOTPPromiseReject;
    let forgeRockPromiseResolve;
    let forgeRockPromiseReject;

    setWebAuthnGlobalState({
      ...webAuthnGlobalState,
      isWebAuthnModalOpen: true,
      isModalLoading: true
    })
    // API getOTP call
    getOTPv2(email)
      .then(({response, error})=> {
      /*
      Series of Promises
      1.- verifyOTPWebAuthnPromise
      2.- forgeRockScriptPromise
      */
        const verifyOTPWebAuthnPromise = new Promise((success, failure)=> {
          verifyOTPPromiseResolve=success; verifyOTPPromiseReject=failure;
        })
        const forgeRockScriptPromise = new Promise((success, failure)=> {
          forgeRockPromiseResolve=success; forgeRockPromiseReject=failure;
        })

        if(response?.data?.encryptedPayload){
          setWebAuthnGlobalState({
            // Modal State
            ...webAuthnGlobalState,
            isModalLoading: false,
            isWebAuthnModalOpen: true,
            // payload state
            getOTPEncryptedPayload: response.data.encryptedPayload,
            // promises state
            verifyOTPWebAuthnPromise: verifyOTPWebAuthnPromise,
            forgeRockScriptPromise: forgeRockScriptPromise,
            // Resolve / Reject functions
            verifyOTPPromiseResolve: verifyOTPPromiseResolve,
            verifyOTPPromiseReject: verifyOTPPromiseReject,
            forgeRockPromiseResolve: forgeRockPromiseResolve,
            forgeRockPromiseReject: forgeRockPromiseReject
          })
        } else if (error) {
          setWebAuthnGlobalState({
            ...webAuthnGlobalState,
            isModalLoading: false,
            isWebAuthnModalOpen: true
          })
          const errorMessage = handleApiError(error, "passwordless enrollment");
          setErrors([errorMessage]);
        }
      })
    // Yet to handle logic errors
      .catch(e => console.log(e))
  }

  const messageSelection = () => {
    let phrase;

    if (isWebAuthnEnabledAndCompatible && isEmailEnrolledInWebauthn(email, nativeWebAuthnData)) {
      phrase = MESSAGE.WEBAUTHN_MESSAGE_ENROLLED_DEVICE;
    }

    if (!isWebAuthnEnabledAndCompatible) {
      phrase = MESSAGE.PASSWORDLESS_MESSAGE_STATUS;
    }
    return phrase
  }

  if (!isEmailEnrolledInWebauthn(email, nativeWebAuthnData) && isWebAuthnEnabledAndCompatible) {
    return (
      <>
        <div className="col__12-12 u--paddingNone sui-grid sui-gap-1 sui-grid-cols-12 sm:sui-grid-cols-12  md:sui-grid-cols-12 lg:sui-grid-cols-6">
          <div className="sui-col-start-1 sui-col-span-12 sm:sui-col-span-2">
            <Typography variant="body-base" weight="bold"> Sign In Options </Typography>
          </div>
          <div className="sui-col-span-12 lg:sui-pl-3 sm:sui-col-start-4 sui-col-span-12 sm:sui-col-span-10 lg:sui-col-span-10  lg:sui-col-start-3">
            <WebAuthnInteractionContainer
                      openWebAuthnModal={openWebAuthnModal}
                      webAuthnGlobalState={webAuthnGlobalState}
                      setWebAuthnGlobalState={setWebAuthnGlobalState}
                      email={email}
                      setErrors={setErrors}
                      errorCleanUp={errorCleanUp}
                      setFlow={setFlow}
                      flow={flow}
                      errors={errors} />
          </div>
          <div className="sui-col-span-12 lg:sui-pl-3 sm:sui-col-start-4 lg:sui-col-start-3">
            <PasswordlessUnenrollment />
          </div>
        </div>
      </>
    )
  } else {
    return(
      <div className="col__12-12 u--paddingNone-horizontal">
        <div>
          <div className={"col__12-12 u--paddingNone u--paddingXsmall-top security-page-sub-header-text"}>
            <Typography variant="body-base" weight="bold"> Sign In Options </Typography>
            { messageSelection() }
          </div>
          <PasswordlessUnenrollment />
        </div>
      </div>
    )
  }

}

const PasswordSection = ({onEditPassword, isEditingPassword, onDoneEditing, isUserPasswordless }) => {
  if(isWebAuthnEnabled() && isUserPasswordless) {
    return <PasswordOptionsWebAuthnUpgrade />;
  } else if (isFeatureSwitchEnabled(FEATURE_SWITCHES.ENABLE_PWDLESS_UNENROLL) && isUserPasswordless) {
    return <PasswordOptions />;
  } else if (isEditingPassword) {
    return <PasswordEdit onDoneEditing={onDoneEditing}/>;
  } else {
    return <PasswordDisplay onEditPassword={onEditPassword}/>
  }
}

export default PasswordSection;